/** Deliberately restricted to the two reviewed Kuhn programs. This is a translation
 * of an existing operation, not a general G-code editor or a collision planner. */
export const KUHN_PROGRAM_DIGESTS: Record<number, string> = {
  140: 'be79cef25517182221787518f3f33ccaade604099d26de398c43069928abe8cb',
  150: 'ed6ce57322c3f329cb9a9d56f955377a13fc4107e9eccfb1e60dce11fecbda25',
};
export const KUHN_SUB_DIGESTS: Record<number, string> = {
  100: 'd58dd0d727d4902ef94ccdc725af71781bddbd6d42f453e00217b026a9a1afc7',
  108: '63ed032203afc20250891f3417f098575eaa7bd161f68f5772affff4ce5d6fee',
};

export interface MotionmasterGridPlacement {
  xCells: number;
  yCells: number;
  pitch: number;
}
export interface MotionmasterKuhnPlacement {
  text: string;
  dx: number;
  dy: number;
  /** Source line classifications; machine anchors remain byte-for-byte intact. */
  fixtureLines: number[];
  fixedLines: number[];
}

const numberWord = /([A-Z])\s*([-+]?(?:\d+\.?\d*|\.\d+))/g;
const body = (line: string) => line.split(';', 1)[0].trim();

export function translateKuhnProgram(
  source: { text: string; programNumber: number; digest: string; versionId: string },
  placement: MotionmasterGridPlacement,
): MotionmasterKuhnPlacement {
  if (KUHN_PROGRAM_DIGESTS[source.programNumber] !== source.digest) throw new Error('This source version has not been reviewed for relocation.');
  if (![placement.xCells, placement.yCells].every(v => Number.isInteger(v) && Math.abs(v) <= 12) ||
      !Number.isFinite(placement.pitch) || placement.pitch <= 0 || placement.pitch > 20) throw new Error('Invalid grid placement.');
  const lines = source.text.replace(/\r\n/g, '\n').replace(/\n$/, '').split('\n');
  const opening = lines.findIndex(l => body(l) === 'G0 X44.9796 Y120 Z0 U0 V0');
  const closing = lines.findIndex((l, i) => i > opening &&
    (source.programNumber === 140 ? body(l) === 'G0 X44.9796 Y120 Z0 U0 V0' : body(l) === 'G0 Y120 Z0 U0 V0'));
  if (opening < 0 || closing <= opening + 1 || !lines.slice(closing + 1).some(l => body(l) === 'M30')) throw new Error('Machine anchor moves have changed.');
  const dx = placement.xCells * placement.pitch, dy = placement.yCells * placement.pitch;
  let absolute = true;
  const fixtureLines: number[] = [], fixedLines: number[] = [];
  const shifted = lines.map((line, index) => {
    if (index <= opening || index >= closing) {
      if (/^[GM]/.test(body(line))) fixedLines.push(index + 1);
      return line;
    }
    const code = body(line);
    if (!code) return line;
    if (/^\(PCALL(?:100|108),D\.39,T\.25\)$/.test(code)) {
      absolute = true; // both reviewed hole routines return in G90
      fixtureLines.push(index + 1);
      return line;
    }
    if (code.startsWith('(') || /[=*/]/.test(code)) throw new Error(`Unsupported block at line ${index + 1}.`);
    const words = [...code.matchAll(numberWord)];
    const gs = words.filter(m => m[1] === 'G').map(m => Number(m[2]));
    if (gs.some(g => ![0, 1, 2, 3, 4, 5, 7, 9, 17, 18, 19, 70, 90, 91].includes(g))) throw new Error(`Unsupported G code at line ${index + 1}.`);
    for (const g of gs) { if (g === 90) absolute = true; if (g === 91) absolute = false; }
    if (gs.includes(9) && !absolute) throw new Error('Incremental G9 relocation is not supported.');
    fixtureLines.push(index + 1);
    const commentAt = line.indexOf(';');
    const prefix = commentAt < 0 ? line : line.slice(0, commentAt);
    const comment = commentAt < 0 ? '' : line.slice(commentAt);
    return prefix.replace(numberWord, (token, axis: string, value: string) => {
      // G9 I/J are an absolute intermediate POINT, unlike G2/G3 center offsets.
      const delta = axis === 'X' || (axis === 'I' && gs.includes(9)) ? dx
        : axis === 'Y' || (axis === 'J' && gs.includes(9)) ? dy : 0;
      if (!absolute || !delta) return token;
      const n = Number(value) + delta;
      return axis + (Math.abs(n) < 0.0000001 ? '0' : n.toFixed(4).replace(/\.?0+$/, ''));
    }) + comment;
  });
  // Do not leave the original fixture-location comment describing the moved setup.
  const location = shifted.findIndex(l => l.trim() === ';D6,D7,E6,E7');
  if (location < 0) throw new Error('Source mounting-pattern reference is missing.');
  shifted[location] = `;SOURCE PATTERN D6,D7,E6,E7; MOVE X${dx.toFixed(4)} Y${dy.toFixed(4)} IN`;
  const metadata = [
    ';KUHN RELOCATION DRAFT - PHYSICAL SETUP / CLEARANCE NOT VERIFIED',
    `;SOURCE P${String(source.programNumber).padStart(6, '0')} ${source.versionId}`,
    `;SOURCE SHA256 ${source.digest}`,
    `;GRID X${placement.xCells} Y${placement.yCells} PITCH ${placement.pitch} IN`,
    ';+X / +Y ARE MACHINE DIRECTIONS. SAME FIXTURE ORIENTATION AND HEIGHT.',
    ';MACHINE ANCHORS FIXED; FIXTURE APPROACH, CUT AND WITHDRAWAL TRANSLATED.',
    ';EXISTING PCALL100/108 REQUIRED; CHECK CONTROL COPIES BEFORE RUNNING.',
    ';NO FULL HEAD / TABLE / OTHER FIXTURE COLLISION CHECK.',
  ];
  shifted[0] = `%KN${source.programNumber === 140 ? '1040' : '1050'} GRID DRAFT,MX,`;
  // Make the baseline's existing inch/absolute assumptions explicit. No new F/S.
  shifted.splice(1, 0, ...metadata, 'G70 G90 G17');
  return { text: shifted.join('\n') + '\n', dx, dy, fixtureLines, fixedLines };
}

/** These exact bodies are interpreted for preview; any changed macro stays unexpanded. */
export function verifiedKuhnHolePlane(number: number, text: string | undefined): 17 | 19 | null {
  if (!text) return null;
  const clean = text.split(/\r?\n/).filter(l => !l.startsWith('%')).map(body).join('').replace(/\s/g, '').toUpperCase();
  const common = '(A=((D-T)/2))(B=A/2)';
  const expected = number === 100
    ? `(SUB100)${common}G17G91G2X0YP0I0JP1F50G2X0Y0I0J-P0G2X0Y-P0I0J-P1G90(RET)`
    : number === 108 ? `(SUB108)${common}G19G91G2Y0ZP0J0KP1G2Y0Z0J0K-P0G2Y0Z-P0J0K-P1G90(RET)` : '';
  return clean === expected ? (number === 100 ? 17 : 19) : null;
}
